/**
 * Route guards enforcing the auth flow of `docs/AUTH.md`:
 * unauthenticated → `/login`; authenticated but profile incomplete →
 * `/onboarding`; authenticated, complete but not validated → `/pending`.
 */
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext';
import { t } from '../i18n/fr';

/** Full-page loading state shown while the initial session check resolves. */
export function LoadingScreen() {
  return (
    <div className="app__loading" role="status">
      {t('common.loading')}
    </div>
  );
}

export interface GuardProps {
  children: ReactNode;
}

/**
 * Require a signed-in session; redirect anonymous visitors to `/login`,
 * preserving the page they were headed to.
 *
 * @param props - The guarded content.
 * @returns The children, a redirect, or a loading placeholder.
 */
export function RequireAuth({ children }: GuardProps) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return <LoadingScreen />;
  }
  if (status === 'anonymous') {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return children;
}

/**
 * Require a signed-in session with a complete profile and a validated
 * account — the gate for every screen of the main app shell.
 *
 * @param props - The guarded content.
 * @returns The children, a redirect to the right step of onboarding, or a
 *   loading placeholder.
 */
export function RequireFullAccess({ children }: GuardProps) {
  const { status, user, profileComplete } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return <LoadingScreen />;
  }
  if (status === 'anonymous') {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  if (!profileComplete) {
    return <Navigate to="/onboarding" replace />;
  }
  if (!user?.is_validated) {
    return <Navigate to="/pending" replace />;
  }
  return children;
}

/**
 * Redirect an already fully-authenticated volunteer away from `/login`
 * (or another public-only page) to the app's home route.
 *
 * @param props - The guarded content.
 * @returns The children, or a redirect to `/`.
 */
export function RedirectIfFullyAuthenticated({ children }: GuardProps) {
  const { status, user, profileComplete } = useAuth();

  if (status === 'authenticated' && profileComplete && user?.is_validated) {
    return <Navigate to="/" replace />;
  }
  return children;
}
